import { Box, Typography } from "@mui/material";
import React, { useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import ThumbsResponse from "../ThumbsResponse/ThumbsResponse";
import { ChatBubble } from "./ChatBubble";
import ChatSourcesList from "./ChatSourcesList";
import { CHATBOT_STOPPED_MESSAGE, ChatTurn } from "./chatBotTypes";

type Props = {
  turn: ChatTurn;
  authId: string;
  platformId?: string;
  leagueId?: string;
  teamName?: string;
  onShareMessage: (messageId: string) => void;
  isCheckmark?: boolean;
  /** Latest completed turn always shows thumbs; older turns reveal on desktop hover only */
  isLatestTurn?: boolean;
  /** Ref attached only on the latest history turn (for pin-to-top scroll) */
  turnRef?: React.Ref<HTMLDivElement>;
};

const areTurnPropsEqual = (prev: Props, next: Props) =>
  prev.turn === next.turn &&
  prev.authId === next.authId &&
  prev.platformId === next.platformId &&
  prev.leagueId === next.leagueId &&
  prev.teamName === next.teamName &&
  prev.onShareMessage === next.onShareMessage &&
  prev.isCheckmark === next.isCheckmark &&
  prev.isLatestTurn === next.isLatestTurn &&
  prev.turnRef === next.turnRef;

const ChatHistoryTurn = ({
  turn,
  authId,
  platformId,
  leagueId,
  teamName,
  onShareMessage,
  isCheckmark,
  isLatestTurn = false,
  turnRef,
}: Props) => {
  const handleShare = useCallback(() => {
    onShareMessage(turn.messageId || "");
  }, [onShareMessage, turn.messageId]);

  return (
    <Box
      ref={turnRef}
      // data-latest drives CSS so thumbs visibility updates reliably when a turn stops being latest
      data-latest={isLatestTurn ? "true" : "false"}
      sx={{
        // Gap between turns so previous thumbs don't peek when the next turn is pinned to top
        mb: 1,
        // Desktop (real hover): hide thumbs on non-latest turns until hovered.
        // Touch / no-hover devices keep thumbs always visible (unchanged UX).
        // Latest turn ([data-latest="true"]) always shows thumbs.
        "@media (hover: hover) and (pointer: fine)": {
          '&[data-latest="false"] .chat-turn-thumbs': {
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.15s ease",
          },
          '&[data-latest="false"]:hover .chat-turn-thumbs': {
            opacity: 1,
            pointerEvents: "auto",
          },
        },
      }}
    >
      {turn.userTranscript && turn.userTranscript.trim().length > 0 && (
        <ChatBubble role="user">
          <Typography
            variant="h5"
            sx={{
              color: PureLightThemeCaaS.colors.alpha.white[100],
              fontSize: "14px",
              fontWeight: 500,
              display: "block",
            }}
          >
            {turn.userTranscript}
          </Typography>
        </ChatBubble>
      )}

      <ChatBubble role="assistant">
        {turn.assistantTranscript === CHATBOT_STOPPED_MESSAGE ? (
          <Typography
            variant="h4"
            fontSize="14px"
            p={0}
            sx={{
              color: PureLightThemeCaaS.colors.alpha.white[50],
              fontStyle: "italic",
              fontWeight: "normal",
            }}
          >
            {CHATBOT_STOPPED_MESSAGE}
          </Typography>
        ) : (
          <Typography
            variant="h4"
            fontSize="16px"
            color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}
            p={0}
          >
            <ReactMarkdown
              className={`${styles.reactMarkdownStart} ${styles.noPaddingOverride}`}
              remarkPlugins={[remarkGfm]}
            >
              {turn.assistantTranscript}
            </ReactMarkdown>
          </Typography>
        )}
      </ChatBubble>

      {turn.timestamp &&
        !(turn.groundingSources && turn.groundingSources.length > 0) && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: PureLightThemeCaaS.colors.alpha.white[50],
              fontSize: "11px",
              mt: 0.5,
              mb: 0.5,
              // pl: 1,
            }}
          >
            {new Date(turn.timestamp).toLocaleString()}
          </Typography>
        )}

      {turn.groundingSources && turn.groundingSources.length > 0 && (
        <ChatSourcesList sources={turn.groundingSources} timestamp={turn.timestamp} />
      )}

      {turn.assistantTranscript && (
        <Box className="chat-turn-thumbs">
          <ThumbsResponse
            userId={authId}
            messageId={turn.messageId}
            message={turn.userTranscript}
            response={turn.assistantTranscript}
            section="chatbot"
            platformId={platformId}
            leagueId={leagueId}
            teamName={teamName}
            handleShare={handleShare}
            isCheckmark={isCheckmark}
          />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ChatHistoryTurn, areTurnPropsEqual);
